import type { DataMeta } from '@/api/types'

/*
 * Pure helpers over a server-paged list's pages: the next page's offset, and the loaded
 * pages folded into one list. Shared by every Analytics list the API sorts and pages
 * (competitions, customers), whatever its rows.
 */

/** What every paged list answer carries: its page of rows, the matching total, and why columns are empty. */
export interface PagedList<Row> {
  rows: readonly Row[]
  total: number
  notAvailable: Partial<Record<keyof Row, string>>
}

/**
 * The offset of the page after `page`, asked for at `requested`, or none when it was the
 * last: advanced by the rows the page returned (never by the size asked for), and
 * stopped by an empty page or once `total` is reached. Counted from the offset asked
 * for, so every step moves forward whatever the answer echoes.
 */
export function nextOffset(page: Pick<PagedList<unknown>, 'rows' | 'total'>, requested: number): number | undefined {
  const next = requested + page.rows.length
  return page.rows.length > 0 && next < page.total ? next : undefined
}

export interface FoldedList<Row extends object, List extends PagedList<Row>> {
  /** Every row loaded so far, across pages, each identified row once. */
  rows: Row[]
  /** A row's key for the table: its identity. */
  keyOf: (row: Row) => string
  /** The newest page's answer: the total and the rest of what the route says. */
  latest: List
  /** Every column a loaded page names as not available, and why. */
  notAvailable: List['notAvailable']
  meta: DataMeta
}

/**
 * Folds the loaded pages into one list: a row that moved between pages while they loaded
 * (paging by offset) is shown once, by its identity, which also keys it for the table.
 * So the count shown, and whether the list is complete, are counts of distinct rows.
 */
export function foldPages<Row extends object, List extends PagedList<Row>>(
  pages: readonly { meta: DataMeta; data: List }[],
  identify: (row: Row) => string,
): FoldedList<Row, List> | undefined {
  const first = pages[0]
  const last = pages[pages.length - 1]
  if (!first || !last) return undefined
  const seen = new Set<string>()
  const rows: Row[] = []
  for (const page of pages) {
    for (const row of page.data.rows) {
      const id = identify(row)
      if (seen.has(id)) continue
      seen.add(id)
      rows.push(row)
    }
  }
  const notAvailable = Object.assign({}, ...pages.map((page) => page.data.notAvailable)) as List['notAvailable']
  return { rows, keyOf: identify, latest: last.data, notAvailable, meta: first.meta }
}
